/**
 * useBrokerAccountSelector Hook
 * Reusable hook for multi-broker account selection
 * Used in order forms, portfolio filters, etc.
 * 
 * ⚠️ IMPORTANT: Per ADR-FE-001, this hook only manages UI selection state.
 * All broker account data comes from backend.
 */

import { useCallback, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  toggleAccountSelection,
} from '../../../store/slices/brokerAccountsSlice';
import { BrokerAccount } from '../../../types/brokerAccount';

export interface UseBrokerAccountSelectorOptions {
  /**
   * Filter accounts by broker type
   */
  brokerType?: BrokerAccount['broker_name'];
  /**
   * Filter only active accounts
   */
  activeOnly?: boolean;
  /**
   * Minimum balance required (for order placement)
   */
  minBalance?: number;
}

export function useBrokerAccountSelector(options: UseBrokerAccountSelectorOptions = {}) {
  const dispatch = useAppDispatch();
  const brokerAccounts = useAppSelector((state) => state.brokerAccounts);

  /**
   * Filter accounts based on options
   */
  const filteredAccounts = useMemo(() => {
    let accounts = brokerAccounts.accounts;

    if (options.brokerType) {
      accounts = accounts.filter((acc) => acc.broker_name === options.brokerType);
    }

    if (options.activeOnly) {
      accounts = accounts.filter((acc) => acc.active);
    }

    if (options.minBalance !== undefined) {
      accounts = accounts.filter((acc) => acc.balance >= options.minBalance!);
    }

    return accounts;
  }, [brokerAccounts.accounts, options.brokerType, options.activeOnly, options.minBalance]);

  /**
   * Get selected accounts from filtered list
   */
  const selectedAccounts = useMemo(() => {
    return filteredAccounts.filter((acc) =>
      brokerAccounts.selectedAccountIds.includes(acc.id)
    );
  }, [filteredAccounts, brokerAccounts.selectedAccountIds]);

  /**
   * Toggle account selection
   */
  const toggleAccount = useCallback(
    (accountId: string) => {
      dispatch(toggleAccountSelection(accountId));
    },
    [dispatch]
  );

  /**
   * Select all filtered accounts
   */
  const selectAll = useCallback(() => {
    // Select all filtered accounts
    filteredAccounts.forEach((acc) => {
      if (!brokerAccounts.selectedAccountIds.includes(acc.id)) {
        dispatch(toggleAccountSelection(acc.id));
      }
    });
  }, [dispatch, filteredAccounts, brokerAccounts.selectedAccountIds]);

  /**
   * Deselect all filtered accounts
   */
  const deselectAll = useCallback(() => {
    // Deselect all filtered accounts
    filteredAccounts.forEach((acc) => {
      if (brokerAccounts.selectedAccountIds.includes(acc.id)) {
        dispatch(toggleAccountSelection(acc.id));
      }
    });
  }, [dispatch, filteredAccounts, brokerAccounts.selectedAccountIds]);

  /**
   * Check if account is selected
   */
  const isSelected = useCallback(
    (accountId: string) => {
      return brokerAccounts.selectedAccountIds.includes(accountId);
    },
    [brokerAccounts.selectedAccountIds]
  );

  /**
   * Check if all filtered accounts are selected
   */
  const allSelected = useMemo(() => {
    if (filteredAccounts.length === 0) return false;
    return filteredAccounts.every((acc) =>
      brokerAccounts.selectedAccountIds.includes(acc.id)
    );
  }, [filteredAccounts, brokerAccounts.selectedAccountIds]);

  /**
   * Check if some (but not all) filtered accounts are selected
   */
  const someSelected = useMemo(() => {
    const selectedCount = filteredAccounts.filter((acc) =>
      brokerAccounts.selectedAccountIds.includes(acc.id)
    ).length;
    return selectedCount > 0 && selectedCount < filteredAccounts.length;
  }, [filteredAccounts, brokerAccounts.selectedAccountIds]);

  return {
    accounts: filteredAccounts,
    selectedAccounts,
    selectedAccountIds: selectedAccounts.map((acc) => acc.id),
    toggleAccount,
    selectAll,
    deselectAll,
    isSelected,
    allSelected,
    someSelected,
    totalCount: filteredAccounts.length,
    selectedCount: selectedAccounts.length,
  };
}

